import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useScreenSize } from '@/hooks';
import { getSocket, encode, decode, C, storage, getUID } from '@/utils';
import { getVipLevelDetails } from '../calculateLevel';
import StarIcon from './StarIcon';
import LevelsAccordion from './LevelsAccordion/LevelsAccordion';
import Button from '@/components/common/Button/Button';
import Modal from '@/components/common/Modal/Modal';
import { gsap } from 'gsap';
import styles from './LoggedInVip.module.css';

/**
 * Logged In VIP Component
 * Modal for VIP dashboard - opens via query params: /vip-club?modal=vip
 * Similar to AuthModal pattern - full screen on mobile and desktop
 * Complete VIP dashboard with bonus table and level progression
 */
const LoggedInVip = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const modal = searchParams.get('modal');
  const { isMobile } = useScreenSize();
  const [isOpen, setIsOpen] = useState(false);
  const [vipDetails, setVipDetails] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [showLevelSystem, setShowLevelSystem] = useState(false);
  const [previousVipLevel, setPreviousVipLevel] = useState(null);
  const progressAnimationRef = useRef(null);
  const vipTitleRef = useRef(null);
  const vipBadgeRef = useRef(null);
  const socket = getSocket();

  // VIP Levels configuration
  const levels = [
    { name: 'Bronze', icon: '/assets/images/vip/bronze.webp' },
    { name: 'Silver', icon: '/assets/images/vip/badge-silver.webp' },
    { name: 'Gold', icon: '/assets/images/vip/gold.webp' },
    { name: 'Platinum I', icon: '/assets/images/vip/pl.png' },
    { name: 'Diamond I', icon: '/assets/images/vip/pl.png' },
    { name: 'Diamond II', icon: '/assets/images/vip/d1.webp' },
    { name: 'Diamond III', icon: '/assets/images/vip/d1.webp' }
  ];

  // Bonus Types configuration
  const bonusTypes = [
    { name: 'Level-up Bonus', icon: '/assets/images/vip/lu.webp' },
    { name: 'Coin Drops', icon: '/assets/images/vip/cd.webp' },
    { name: 'Private Chat', icon: '/assets/images/vip/pc.webp' },
    { name: 'Tips', icon: '/assets/images/vip/tips.webp' },
    { name: 'Recharge', icon: '/assets/images/vip/rc.webp' },
    { name: 'Weekly Cashback', icon: '/assets/images/vip/wc.webp' }
  ];

  // Level stars configuration
  const levelStars = {
    Bronze: {
      'Level-up Bonus': 'bronze',
      'Coin Drops': 'bronze',
      'Private Chat': null,
      'Tips': null,
      'Recharge': null,
      'Weekly Cashback': null
    },
    Silver: {
      'Level-up Bonus': 'silver',
      'Coin Drops': 'silver',
      'Private Chat': 'silver',
      'Tips': null,
      'Recharge': null,
      'Weekly Cashback': null
    },
    Gold: {
      'Level-up Bonus': 'gold',
      'Coin Drops': 'gold',
      'Private Chat': 'gold',
      'Tips': 'gold',
      'Recharge': null,
      'Weekly Cashback': null
    },
    'Platinum I': {
      'Level-up Bonus': 'platinum',
      'Coin Drops': 'platinum',
      'Private Chat': 'platinum',
      'Tips': 'platinum',
      'Recharge': 'platinum',
      'Weekly Cashback': null
    },
    'Diamond I': {
      'Level-up Bonus': 'diamond',
      'Coin Drops': 'diamond',
      'Private Chat': 'diamond',
      'Tips': 'diamond',
      'Recharge': 'diamond',
      'Weekly Cashback': 'diamond'
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Remove query params and go back
    navigate(-1);
  };

  useEffect(() => {
    // Check if modal should be open based on query params
    if (modal === 'vip' || modal === 'true') {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [modal]);

  // ESC key handling is now done by Modal component

  // Animate progress bar
  const animateProgress = (targetProgress) => {
    if (progressAnimationRef.current) {
      cancelAnimationFrame(progressAnimationRef.current);
    }

    let currentProgress = 0;
    const duration = 1000; // 1 second animation
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);
      
      currentProgress = progressRatio * targetProgress;
      setProgress(currentProgress);

      if (progressRatio < 1) {
        progressAnimationRef.current = requestAnimationFrame(animate);
      }
    };

    progressAnimationRef.current = requestAnimationFrame(animate);
  };

  // Parse wagered amount
  const parseWageredAmount = (amountString) => {
    if (!amountString) return 0;
    let cleanedAmount = amountString.toString().replace(/,/g, '');
    let parsedAmount = parseFloat(cleanedAmount);
    return isNaN(parsedAmount) ? 0 : parsedAmount;
  };

  // Animate VIP level increase with GSAP - Slow and smooth animation
  const animateLevelUp = () => {
    if (!vipTitleRef.current || !vipBadgeRef.current) return;

    // Create timeline for smooth sequential animation - SLOW VERSION
    const tl = gsap.timeline({ defaults: { ease: 'power2.inOut' } });

    // Animate VIP title with scale, glow - SLOW
    tl.to(vipTitleRef.current, {
      scale: 1.4,
      duration: 1.2, // Slower scale up
      ease: 'power2.out',
    })
    .to(vipTitleRef.current, {
      textShadow: '0 0 30px rgba(212, 175, 55, 1), 0 0 60px rgba(212, 175, 55, 0.8), 0 0 90px rgba(212, 175, 55, 0.6)',
      color: '#FFD700',
      duration: 0.8, // Slower glow transition
      ease: 'power2.inOut',
    })
    .to(vipTitleRef.current, {
      scale: 1,
      textShadow: '0 0 15px rgba(212, 175, 55, 0.6)',
      color: 'var(--color-vip)',
      duration: 1.5, // Slower return with elastic bounce
      ease: 'elastic.out(1, 0.4)',
    });

    // Animate VIP badge with scale and glow - SLOW
    gsap.to(vipBadgeRef.current, {
      scale: 1.5,
      duration: 1.2, // Slower scale up
      ease: 'power2.out',
      filter: 'drop-shadow(0px 0px 40px rgba(212, 175, 55, 1)) brightness(1.5)',
    });

    gsap.to(vipBadgeRef.current, {
      scale: 1,
      duration: 1.5, // Slower return
      ease: 'elastic.out(1, 0.4)',
      filter: 'drop-shadow(0px 4px 8px rgba(212, 175, 55, 0.3))',
      delay: 1.2,
    });

    // Add particle effect - SLOW and smooth
    const particles = 25;
    for (let i = 0; i < particles; i++) {
      const particle = document.createElement('div');
      particle.className = styles.levelUpParticle;
      
      // Position particles around VIP badge/title area
      const badgeRect = vipBadgeRef.current?.getBoundingClientRect();
      const titleRect = vipTitleRef.current?.getBoundingClientRect();
      const centerX = badgeRect ? badgeRect.left + badgeRect.width / 2 : window.innerWidth / 2;
      const centerY = badgeRect ? badgeRect.top + badgeRect.height / 2 : window.innerHeight / 2;
      
      particle.style.left = `${centerX}px`;
      particle.style.top = `${centerY}px`;
      particle.style.position = 'fixed';
      document.body.appendChild(particle);

      // Slow particle animation
      gsap.to(particle, {
        x: (Math.random() - 0.5) * 300,
        y: (Math.random() - 0.5) * 300,
        opacity: 0,
        scale: Math.random() * 0.8 + 0.4,
        rotation: Math.random() * 360,
        duration: 2.5, // Slower particle movement
        ease: 'power2.out',
        delay: Math.random() * 0.3,
        onComplete: () => {
          if (particle.parentNode) {
            document.body.removeChild(particle);
          }
        },
      });
    }
  };

  // Fetch user info from socket
  useEffect(() => {
    if (!isOpen || !socket) return;

    const getUserInfo = (data) => {
      try {
        const decodedData = decode(data);
        if (decodedData && decodedData.status) {
          const waggerAmount = parseWageredAmount(decodedData.profit);
          if (waggerAmount !== null && waggerAmount !== undefined) {
            const newVipDetails = getVipLevelDetails(waggerAmount);
            const newLevel = newVipDetails.previousVipLevel;
            
            // Check if VIP level increased
            if (previousVipLevel !== null && newLevel && previousVipLevel !== newLevel) {
              // Extract level numbers for comparison
              const oldLevelNum = parseInt(previousVipLevel.replace(/\D/g, '')) || 0;
              const newLevelNum = parseInt(newLevel.replace(/\D/g, '')) || 0;
              
              if (newLevelNum > oldLevelNum) {
                // Level increased - trigger slow animation
                setTimeout(() => {
                  animateLevelUp();
                }, 300);
              }
            }
            
            // Set previous level (initialize on first load)
            if (previousVipLevel === null) {
              setPreviousVipLevel(newLevel);
            } else if (newLevel !== previousVipLevel) {
              setPreviousVipLevel(newLevel);
            }
            setVipDetails(newVipDetails);
            // Animate progress based on completion percentage
            animateProgress(parseFloat(newVipDetails.completionPercentage || 0));
          }
        }
      } catch (error) {
        console.error('Error processing user info:', error);
      }
    };

    socket.on(C.USER_INFO, getUserInfo);

    // Request user info
    const uid = getUID();
    if (uid) {
      socket.emit(C.USER_INFO, encode({
        id: uid,
        coin: storage.getKey('coin') || '',
        rate: null,
        game: 'all',
        first: true
      }));
    }

    // Cleanup
    return () => {
      if (socket) {
        socket.off(C.USER_INFO, getUserInfo);
      }
      if (progressAnimationRef.current) {
        cancelAnimationFrame(progressAnimationRef.current);
      }
    };
  }, [isOpen, socket]);

  const handleBack = () => {
    handleClose();
  };

  const getStarImage = (level, bonusType) => {
    const levelConfig = levelStars[level] || {};
    const starColor = levelConfig[bonusType];
    return <StarIcon color={starColor} />;
  };

  const maxPage = Math.ceil((levels.length - 1) / 2);

  const handleNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, maxPage - 1));
  };

  const handlePrev = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };

  const visibleLevels = levels.slice(1).slice(currentPage * 2, (currentPage * 2) + 2);

  // Custom header content with back button
  const headerContent = (
    <div className={styles.vipHeaderContent}>
      <button className={styles.backButton} onClick={handleBack}>
        <ChevronLeft size={24} />
      </button>
      <h1 className={styles.vipTitle}>VIP Bonus</h1>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      showCloseButton={true}
      headerContent={headerContent}
      className={styles.vipModal}
    >
      <div className={styles.container}>

          <div className={styles.topCard}>
            <div className={styles.vipContent}>
              <div className={styles.vipInfo}>
                <h2 className={styles.vipTitle}>
                  {vipDetails?.previousVipLevel?.replace('IP', '').replace(' ', '') || 'V0'}
                </h2>
                <p className={styles.xpText}>
                  Current XP: <span>{vipDetails?.currentWagger || '0'}</span> / 
                  Total Wager: <span>{vipDetails?.currentWagger || '0.00'} USD</span>
                </p>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progress}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className={styles.xpText}>
                  {vipDetails?.waggerNeededForNextLevel || '1'} XP until {vipDetails?.vipLevel || 'VIP1'}
                </p>
              </div>
            </div>
            <div ref={vipBadgeRef} className={styles.vipImage}>
              {/* VIP Badge Image */}
            </div>
          </div>

          <Button
            variant="secondary"
            size="md"
            fullWidth
            onClick={() => setShowLevelSystem(true)}
            className={styles.levelButton}
          >
            Learn More VIP Level Up System
          </Button>

          {!showLevelSystem ? (
            <div className={styles.scrollableContent}>
              <div className={styles.bonusTable}>
                <div className={styles.tableHeader}>
                  <h3 className={styles.tableTitle}>Exclusive VIP Bonus</h3>
                </div>

              <div className={styles.tableContainer}>
                <div className={styles.fixedColumn}>
                  <div className={styles.columnHeader}>
                    <span className={styles.bonusTypeLabel}>Bonus Type</span>
                  </div>
                  {bonusTypes.map(bonus => (
                    <div key={bonus.name} className={styles.bonusRow1}>
                      <img src={bonus.icon} alt={bonus.name} />
                      <span>{bonus.name}</span>
                    </div>
                  ))}
                </div>

                <button
                  className={`${styles.arrowButton} ${styles.arrowLeft}`}
                  onClick={handlePrev}
                  disabled={currentPage === 0}
                  aria-label="Previous levels"
                >
                  <ChevronLeft size={15} />
                </button>

                <div className={styles.scrollableColumns}>
                  <div className={styles.column}>
                    <div className={styles.columnHeader}>
                      <img src={levels[0].icon} alt={levels[0].name} />
                      {levels[0].name}
                    </div>
                    {bonusTypes.map(bonus => (
                      <div key={`bronze-${bonus.name}`} className={styles.bonusRow}>
                        {getStarImage('Bronze', bonus.name)}
                      </div>
                    ))}
                  </div>

                  {visibleLevels.map(level => (
                    <div key={level.name} className={styles.column}>
                      <div className={styles.columnHeader}>
                        <img src={level.icon} alt={level.name} />
                        {level.name}
                      </div>
                      {bonusTypes.map(bonus => (
                        <div key={`${level.name}-${bonus.name}`} className={styles.bonusRow}>
                          {getStarImage(level.name, bonus.name)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <button
                  className={`${styles.arrowButton} ${styles.arrowRight}`}
                  onClick={handleNext}
                  disabled={currentPage >= maxPage - 1}
                  aria-label="Next levels"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </div>
          ) : (
            <LevelsAccordion onBack={() => setShowLevelSystem(false)} />
          )}
      </div>
    </Modal>
  );
};

export default LoggedInVip;
